import { PresenceFilter } from '@/contracts/marketplace/reviews';
import clsx from 'clsx';
import { FC, ReactNode } from 'react';

export { ClearFilterButton } from '@/ui/ClearFilterButton';

const cyclePresenceFilter = (value: PresenceFilter | undefined): PresenceFilter | undefined => {
    if (value === 'with') {
        return 'without';
    }

    if (value === 'without') {
        return undefined;
    }

    return 'with';
};

const iconBaseClassName = 'h-3.5 w-3.5';

export const CameraIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={iconBaseClassName}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h3l1.4-2h7.2L17 8h3v10H4V8z" />
        <circle cx="12" cy="13" r="3.2" />
    </svg>
);

export const UserIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={iconBaseClassName}>
        <circle cx="12" cy="8" r="3.2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.5 18.2c1.8-2.7 4-4 6.5-4s4.7 1.3 6.5 4" />
    </svg>
);

export const ReviewTextIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={iconBaseClassName}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 6.5h14M5 11.5h14M5 16.5h9" />
    </svg>
);

export const AnswerTextIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={iconBaseClassName}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12v8H9l-3 3V7z" />
    </svg>
);

export const AiIcon = () => <span className="text-[10px] font-semibold tracking-wide">AI</span>;

export const SearchIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-3.5 w-3.5 text-slate-400"
        aria-hidden
    >
        <circle cx="11" cy="11" r="6.5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="m16 16 4.2 4.2" />
    </svg>
);

type PresenceFilterIconButtonProps = {
    ariaLabel: string;
    icon: ReactNode;
    value: PresenceFilter | undefined;
    onChange: (value: PresenceFilter | undefined) => void;
};

export const PresenceFilterIconButton: FC<PresenceFilterIconButtonProps> = ({ ariaLabel, icon, value, onChange }) => {
    const modeLabel = value === 'with' ? 'есть' : value === 'without' ? 'нет' : 'любой';

    return (
        <button
            type="button"
            aria-label={`${ariaLabel}: ${modeLabel}`}
            title={`${ariaLabel}: ${modeLabel}`}
            className={clsx(
                'relative flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md border transition-colors',
                value === 'with' && 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                value === 'without' && 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100',
                !value && 'border-slate-300 bg-white text-slate-500 hover:bg-slate-100',
            )}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onChange(cyclePresenceFilter(value));
            }}
        >
            {icon}
            {value && (
                <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-slate-700 text-[9px] text-white">
                    {value === 'with' ? '+' : '-'}
                </span>
            )}
        </button>
    );
};
